// خدمة WhatsApp هجينة تعمل في المتصفح والخادم
import { MaintenanceRecord } from '../utils/types';
import { getSystemSettings } from './systemSettingsService';
import { getCustomerById } from './customerService';
import { whatsappTemplatesService, WhatsAppTemplateType } from './whatsappTemplatesService';
import { getCountryByCode, getDefaultCountry } from '../utils/countries';
import { whatsappStatsService } from './whatsappStatsService';

// واجهة بيانات الرسالة
export interface WhatsAppMessageData {
  customerName: string;
  customerPhone: string;
  deviceType: string;
  deviceBrand?: string;
  deviceSize?: string;
  cost?: string;
  orderId?: string;
  orderNumber?: number;
  estimatedDate?: string;
  receiveDate?: string;
  completionDate?: string;
  daysSinceCompletion?: string;
  problemDescription?: string;
  phoneNumber?: string;
  address?: string;
}

// أنواع الرسائل
export type WhatsAppMessageType = 'deviceReceived' | 'deviceInProgress' | 'deviceCompleted' | 'deviceUrgent';

// حالة الخدمة
export interface WhatsAppServiceStatus {
  available: boolean;
  method: 'baileys' | 'wa.me' | 'none';
  connected?: boolean;
  error?: string;
}

// قوالب الرسائل
const messageTemplates = {
  deviceReceived: `🔧 *تم استلام جهازك*

مرحباً {customerName}،

تم استلام جهازك ({deviceBrand} - {deviceSize}) بنجاح في مركز الصيانة.

📋 *تفاصيل الطلب:*
• رقم الطلب: #{orderNumber}
• ماركة الجهاز: {deviceBrand}
• الحجم: {deviceSize}
• التكلفة المبدئية: {cost}
• التاريخ المتوقع للتسليم: {estimatedDate}

سنقوم بفحص الجهاز وإعلامك بالتكلفة النهائية قريباً.
من فضلك وافق بنعم حتي تتلقي اشعارات بانتهاء الصيانه.

شكراً لثقتكم بنا! 🙏`,

  deviceInProgress: `⚙️ *جهازك قيد الصيانة*

مرحباً {customerName}،

جهازك ({deviceBrand} - {deviceSize}) الآن قيد الصيانة.

📋 *تفاصيل الطلب:*
• رقم الطلب: #{orderNumber}
• ماركة الجهاز: {deviceBrand}
• التكلفة المقدرة: {cost}

{callBeforeRepair}

سنعلمك فور انتهاء الصيانة.

شكراً لصبركم! ⏰`,

  deviceCompleted: `مرحباً {customerName}، تم الانتهاء من إصلاح جهازك {deviceBrand}، يرجى المرور لاستلامه في أقرب وقت ممكن. شكراً لثقتكم بنا الكترونيات السواح باب اللوق 01025654218.`,

  deviceUrgent: `🚨 *تذكير عاجل - استلام الجهاز*

مرحباً {customerName}،

جهازك ({deviceBrand} - {deviceSize}) جاهز للاستلام منذ أكثر من يومين.

📋 *تفاصيل الطلب:*
• رقم الطلب: #{orderNumber}
• ماركة الجهاز: {deviceBrand}
• التكلفة النهائية: {cost}

يرجى الحضور لاستلام جهازك في أقرب وقت ممكن لتجنب أي رسوم إضافية.

⏰ *ساعات العمل:* من 9 صباحاً إلى 9 مساءً

للاستفسار: اتصل بنا على الرقم المعتاد.`
};

class HybridWhatsAppService {
  private status: WhatsAppServiceStatus = {
    available: false,
    method: 'none'
  };

  constructor() {
    this.detectAvailableMethod();
  }

  // اكتشاف الطريقة المتاحة
  private async detectAvailableMethod() {
    // نستخدم wa.me links دائماً - طريقة بسيطة وموثوقة
    this.status = {
      available: true,
      method: 'wa.me',
      connected: true
    };
  }

  // الحصول على رقم هاتف العميل
  private async getCustomerPhone(customerId: string): Promise<string> {
    try {
      const customer = await getCustomerById(customerId);
      return customer?.phone || '';
    } catch (error) {
      console.error('خطأ في الحصول على رقم هاتف العميل:', error);
      return '';
    }
  }

  // تنسيق رقم الهاتف حسب إعدادات الدولة
  private async formatPhoneNumber(phoneNumber: string): Promise<string> {
    if (!phoneNumber || typeof phoneNumber !== 'string') {
      throw new Error('رقم الهاتف غير صحيح');
    }

    let cleaned = phoneNumber.replace(/\D/g, '');

    if (!cleaned) {
      throw new Error('رقم الهاتف فارغ بعد التنظيف');
    }

    if (cleaned.length < 8) {
      throw new Error('رقم الهاتف قصير جداً');
    }

    if (cleaned.length > 15) {
      throw new Error('رقم الهاتف طويل جداً');
    }

    try {
      // الحصول على إعدادات النظام
      const settings = await getSystemSettings();
      const countryCode = settings.whatsappCountryCode || '20';

      // إضافة رمز الدولة إذا لم يكن موجوداً
      if (!cleaned.startsWith(countryCode)) {
        if (cleaned.startsWith('0')) {
          cleaned = countryCode + cleaned.substring(1);
        } else {
          cleaned = countryCode + cleaned;
        }
      }

      // التحقق من صحة الرقم النهائي
      if (!/^\d{10,15}$/.test(cleaned)) {
        throw new Error('تنسيق رقم الهاتف غير صحيح');
      }

      return cleaned;
    } catch (error) {
      console.error('Error getting country settings:', error);
      // العودة للافتراضي (مصر) في حالة الخطأ
      const defaultCountry = getDefaultCountry();
      if (!cleaned.startsWith(defaultCountry.code)) {
        if (cleaned.startsWith('0')) {
          cleaned = defaultCountry.code + cleaned.substring(1);
        } else {
          cleaned = defaultCountry.code + cleaned;
        }
      }

      // التحقق من صحة الرقم النهائي
      if (!/^\d{10,15}$/.test(cleaned)) {
        throw new Error('تنسيق رقم الهاتف غير صحيح');
      }

      return cleaned;
    }
  }

  // إعداد الرسالة من القوالب المخصصة
  private async prepareMessage(
    type: WhatsAppMessageType,
    data: WhatsAppMessageData,
    useCallBeforeRepair: boolean = false
  ): Promise<string> {
    try {
      // الحصول على القالب المخصص
      const template = await whatsappTemplatesService.getTemplate(type as WhatsAppTemplateType);

      // التحقق من أن القالب مفعل
      if (!template.enabled) {
        console.warn(`Template ${type} is disabled`);
        return '';
      }

      let message = template.content;

      // استبدال المتغيرات
      message = message
        .replace(/{customerName}/g, data.customerName)
        .replace(/{deviceType}/g, data.deviceType)
        .replace(/{deviceBrand}/g, data.deviceBrand || data.deviceType || 'غير محدد')
        .replace(/{deviceSize}/g, data.deviceSize || 'غير محدد')
        .replace(/{cost}/g, data.cost || 'غير محدد')
        .replace(/{orderId}/g, data.orderNumber?.toString() || data.orderId || 'غير محدد')
        .replace(/{orderNumber}/g, data.orderNumber?.toString() || data.orderId || 'غير محدد')
        .replace(/{estimatedDate}/g, data.estimatedDate || 'سيتم تحديده لاحقاً')
        .replace(/{receiveDate}/g, data.receiveDate || new Date().toLocaleDateString('ar-EG'))
        .replace(/{completionDate}/g, data.completionDate || new Date().toLocaleDateString('ar-EG'))
        .replace(/{daysSinceCompletion}/g, data.daysSinceCompletion || '0')
        .replace(/{problemDescription}/g, data.problemDescription || 'سيتم تحديدها بعد الفحص')
        .replace(/{phoneNumber}/g, data.phoneNumber || '01025654218')
        .replace(/{address}/g, data.address || 'الكترونيات السواح - باب اللوق');

      // إضافة نص الاتصال قبل الإصلاح إذا كان مطلوباً
      if (type === 'deviceInProgress' && useCallBeforeRepair) {
        message = message.replace(
          /{callBeforeRepair}/g,
          '⚠️ *مهم:* سنتصل بك قبل البدء في الإصلاح للموافقة على التكلفة.'
        );
      } else {
        message = message.replace(/{callBeforeRepair}/g, '');
      }

      return message;
    } catch (error) {
      console.error('Error preparing message:', error);
      // العودة للقالب الافتراضي في حالة الخطأ
      return messageTemplates[type] || '';
    }
  }

  // إرسال رسالة باستخدام wa.me
  private async sendViaWaMe(phoneNumber: string, message: string): Promise<boolean> {
    try {
      const formattedNumber = await this.formatPhoneNumber(phoneNumber);

      if (!formattedNumber) {
        throw new Error('رقم الهاتف غير صحيح أو فارغ');
      }

      if (!message.trim()) {
        throw new Error('نص الرسالة فارغ');
      }

      const encodedMessage = encodeURIComponent(message);
      const waUrl = `https://wa.me/${formattedNumber}?text=${encodedMessage}`;

      // التحقق من طول الرابط
      if (waUrl.length > 2048) {
        throw new Error('الرسالة طويلة جداً. يرجى تقصير النص');
      }

      // فتح الرابط في نافذة جديدة
      if (typeof window !== 'undefined') {
        const newWindow = window.open(waUrl, '_blank');

        if (!newWindow) {
          throw new Error('تم حظر النافذة المنبثقة. يرجى السماح بالنوافذ المنبثقة لهذا الموقع');
        }

        return true;
      }

      console.log(`WhatsApp URL: ${waUrl}`);
      return true;
    } catch (error) {
      console.error('خطأ في إرسال رسالة wa.me:', error);
      throw error; // إعادة رمي الخطأ للمعالجة في المستوى الأعلى
    }
  }

  // إرسال رسالة رئيسية
  public async sendMessage(
    type: WhatsAppMessageType,
    data: WhatsAppMessageData,
    useCallBeforeRepair: boolean = false
  ): Promise<boolean> {
    let success = false;
    let error: string | undefined;

    try {
      // التحقق من إعدادات النظام
      const settings = await getSystemSettings();
      if (!settings.whatsappEnabled) {
        error = 'خدمة WhatsApp معطلة في إعدادات النظام';
        console.warn(error);
        return false;
      }

      if (!data.customerPhone) {
        error = 'رقم هاتف العميل غير متوفر';
        console.warn(error);
        return false;
      }

      const message = await this.prepareMessage(type, data, useCallBeforeRepair);

      // إرسال حسب الطريقة المتاحة
      switch (this.status.method) {
        case 'wa.me':
          success = await this.sendViaWaMe(data.customerPhone, message);
          break;

        case 'baileys':
          // TODO: تنفيذ إرسال Baileys في المستقبل
          console.log('Baileys not implemented yet, falling back to wa.me');
          success = await this.sendViaWaMe(data.customerPhone, message);
          break;

        default:
          error = 'لا توجد طريقة متاحة لإرسال WhatsApp';
          console.error(error);
          success = false;
      }
    } catch (err) {
      error = 'خطأ في إرسال رسالة WhatsApp';
      console.error(error, err);
      success = false;
    } finally {
      // تسجيل الإحصائيات
      await whatsappStatsService.logMessage(
        type,
        data.customerName,
        data.customerPhone,
        data.orderId || data.orderNumber?.toString() || 'غير محدد',
        success,
        error
      );
    }

    return success;
  }

  // إرسال رسالة استلام الجهاز
  public async sendDeviceReceivedMessage(maintenance: MaintenanceRecord): Promise<boolean> {
    console.log('📱 HybridWhatsAppService: بدء إرسال رسالة استلام الجهاز');
    console.log('📋 بيانات طلب الصيانة المستلمة:', {
      id: maintenance.id,
      customerId: maintenance.customerId,
      customerName: maintenance.customerName,
      orderNumber: maintenance.orderNumber
    });

    const customerPhone = await this.getCustomerPhone(maintenance.customerId);
    console.log('📞 رقم هاتف العميل:', customerPhone);

    const data: WhatsAppMessageData = {
      customerName: maintenance.customerName,
      customerPhone,
      deviceType: maintenance.deviceType,
      deviceBrand: maintenance.devices?.[0]?.brand || 'غير محدد',
      deviceSize: maintenance.devices?.[0]?.size || 'غير محدد',
      cost: maintenance.initialCost ? `${maintenance.initialCost} جنيه` : 'سيتم تحديدها',
      orderNumber: maintenance.orderNumber,
      estimatedDate: maintenance.estimatedDeliveryDate
        ? new Date(maintenance.estimatedDeliveryDate).toLocaleDateString('ar-EG')
        : 'سيتم تحديده',
      receiveDate: maintenance.receivedAt
        ? new Date(maintenance.receivedAt).toLocaleDateString('ar-EG')
        : new Date().toLocaleDateString('ar-EG'),
      phoneNumber: '01025654218',
      address: 'الكترونيات السواح - باب اللوق'
    };

    console.log('📤 بيانات الرسالة المُعدة للإرسال:', data);

    const result = await this.sendMessage('deviceReceived', data);
    console.log('📨 نتيجة إرسال الرسالة:', result);

    return result;
  }

  // إرسال رسالة بدء الصيانة
  public async sendDeviceInProgressMessage(
    maintenance: MaintenanceRecord,
    estimatedCost?: number,
    problemDescription?: string
  ): Promise<boolean> {
    const customerPhone = await this.getCustomerPhone(maintenance.customerId);
    const data: WhatsAppMessageData = {
      customerName: maintenance.customerName,
      customerPhone,
      deviceType: maintenance.deviceType,
      deviceBrand: maintenance.devices?.[0]?.brand || 'غير محدد',
      deviceSize: maintenance.devices?.[0]?.size || 'غير محدد',
      cost: estimatedCost ? `${estimatedCost} جنيه` : maintenance.estimatedCost ? `${maintenance.estimatedCost} جنيه` : 'سيتم تحديدها',
      orderNumber: maintenance.orderNumber,
      problemDescription: problemDescription || maintenance.issue || 'سيتم تحديدها بعد الفحص',
      phoneNumber: '01025654218'
    };

    return this.sendMessage('deviceInProgress', data, maintenance.requireCallBeforeMaintenance);
  }

  // إرسال رسالة اكتمال الصيانة
  public async sendDeviceCompletedMessage(maintenance: MaintenanceRecord): Promise<boolean> {
    const customerPhone = await this.getCustomerPhone(maintenance.customerId);
    const data: WhatsAppMessageData = {
      customerName: maintenance.customerName,
      customerPhone,
      deviceType: maintenance.deviceType,
      deviceBrand: maintenance.devices?.[0]?.brand || 'غير محدد',
      deviceSize: maintenance.devices?.[0]?.size || 'غير محدد',
      cost: maintenance.finalCost ? `${maintenance.finalCost} جنيه` : 'سيتم تحديدها',
      orderNumber: maintenance.orderNumber,
      completionDate: maintenance.completedAt
        ? new Date(maintenance.completedAt).toLocaleDateString('ar-EG')
        : new Date().toLocaleDateString('ar-EG'),
      phoneNumber: '01025654218',
      address: 'الكترونيات السواح - باب اللوق'
    };

    return this.sendMessage('deviceCompleted', data);
  }

  // إرسال رسالة تذكير عاجل
  public async sendUrgentReminderMessage(maintenance: MaintenanceRecord): Promise<boolean> {
    const customerPhone = await this.getCustomerPhone(maintenance.customerId);

    // حساب عدد الأيام منذ الانتهاء
    const completedDate = maintenance.completedAt ? new Date(maintenance.completedAt) : new Date();
    const currentDate = new Date();
    const daysDiff = Math.floor((currentDate.getTime() - completedDate.getTime()) / (1000 * 60 * 60 * 24));

    const data: WhatsAppMessageData = {
      customerName: maintenance.customerName,
      customerPhone,
      deviceType: maintenance.deviceType,
      deviceBrand: maintenance.devices?.[0]?.brand || 'غير محدد',
      deviceSize: maintenance.devices?.[0]?.size || 'غير محدد',
      cost: maintenance.finalCost ? `${maintenance.finalCost} جنيه` : 'سيتم تحديدها',
      orderNumber: maintenance.orderNumber,
      completionDate: completedDate.toLocaleDateString('ar-EG'),
      daysSinceCompletion: daysDiff.toString(),
      phoneNumber: '01025654218',
      address: 'الكترونيات السواح - باب اللوق'
    };

    return this.sendMessage('deviceUrgent', data);
  }

  // الحصول على حالة الخدمة
  public getStatus(): WhatsAppServiceStatus {
    return { ...this.status };
  }

  // التحقق من التوفر
  public isAvailable(): boolean {
    return this.status.available;
  }
}

// إنشاء مثيل واحد من الخدمة
export const hybridWhatsAppService = new HybridWhatsAppService();
export default hybridWhatsAppService;
